// src/utils/recentItems.js

// ==================================================
// CONFIG
// ==================================================
const RECENT_DAYS = 5;
const RECENT_CUTOFF_MS = RECENT_DAYS * 24 * 60 * 60 * 1000;

// ==================================================
// HELPERS
// ==================================================
function storageKey(userId) {
  return `be_recent_items_${userId}`;
}

function empty() {
  return {
    companies: [],
    contacts: [],
    projects: [],
    prospects: [],
    prospecting_contacts: [],
  };
}

function isRecent(item) {
  if (!item?.viewed_at) return false;
  return Date.now() - new Date(item.viewed_at).getTime() <= RECENT_CUTOFF_MS;
}

// 🔑 NEW — stable identity key for de-duping (prevents "same" item showing twice
// when you sometimes store numeric ids vs external guids, etc.)
function identityKey(type, item) {
  // Prefer stable external ids if present
  if (item?.external_id) return `${type}:external:${item.external_id}`;
  // Fallback to internal id
  if (item?.id) return `${type}:id:${item.id}`;

  // Last resort fallback: name-based
  const name = (item?.name || "").trim().toLowerCase();
  return `${type}:name:${name}`;
}

// ==================================================
// LOAD / SAVE
// ==================================================
function loadRecent(userId) {
  if (typeof window === "undefined" || !userId) {
    return empty();
  }

  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return empty();

    const parsed = JSON.parse(raw);

    return {
      companies: parsed.companies || [],
      contacts: parsed.contacts || [],
      projects: parsed.projects || [],
      prospects: parsed.prospects || [],
      prospecting_contacts: parsed.prospecting_contacts || [],
    };
  } catch (err) {
    return empty();
  }
}

function saveRecent(userId, data) {
  if (typeof window === "undefined" || !userId) return;

  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(data));
  } catch (err) {}
}

// ==================================================
// ADD RECENT ITEM
// type:
// "company" | "contact" | "project" | "prospect" | "prospecting_contact"
// ==================================================
export function addRecentItem(userId, type, item) {
  if (!userId || !item?.id) return;

  const data = loadRecent(userId);

  const stampedItem = {
    ...item,
    viewed_at: new Date().toISOString(),
  };

  // Use stable identity key for de-dupe
  const key = identityKey(type, stampedItem);

  if (type === "company") {
    data.companies = [
      stampedItem,
      ...data.companies.filter((x) => identityKey("company", x) !== key),
    ].slice(0, 10);
  }

  if (type === "contact") {
    data.contacts = [
      stampedItem,
      ...data.contacts.filter((x) => identityKey("contact", x) !== key),
    ].slice(0, 10);
  }

  if (type === "project") {
    data.projects = [
      stampedItem,
      ...data.projects.filter((x) => identityKey("project", x) !== key),
    ].slice(0, 10);
  }

  if (type === "prospect") {
    data.prospects = [
      stampedItem,
      ...data.prospects.filter((x) => identityKey("prospect", x) !== key),
    ].slice(0, 10);
  }

  if (type === "prospecting_contact") {
    data.prospecting_contacts = [
      stampedItem,
      ...data.prospecting_contacts.filter(
        (x) => identityKey("prospecting_contact", x) !== key,
      ),
    ].slice(0, 10);
  }

  saveRecent(userId, data);
}

// ==================================================
// READ RECENT ITEMS (5-DAY FILTER APPLIED)
// ==================================================
export function getRecentItems(userId) {
  const data = loadRecent(userId);

  return {
    companies: data.companies.filter(isRecent),
    contacts: data.contacts.filter(isRecent),
    projects: data.projects.filter(isRecent),
    prospects: data.prospects.filter(isRecent),
    prospecting_contacts: data.prospecting_contacts.filter(isRecent),
  };
}
